import { unwrapData } from './unwrapApiData';

function normalizeRoleSlug(value) {
  if (value == null || value === '') return '';
  return String(value).toLowerCase().trim().replace(/[\s-]+/g, '_');
}

function truthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function roleNameFromEntry(entry) {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object') {
    return entry.name || entry.slug || entry.code || entry.role || '';
  }
  return '';
}

function collectRoleNames(u) {
  const names = [];
  if (typeof u.role === 'string' && u.role.trim()) names.push(u.role.trim());
  if (typeof u.platform_role === 'string') names.push(u.platform_role);
  if (typeof u.user_type === 'string') names.push(u.user_type);
  if (Array.isArray(u.roles)) {
    for (const r of u.roles) names.push(roleNameFromEntry(r));
  }
  return names.map(normalizeRoleSlug).filter(Boolean);
}

function detectSuperAdmin(u) {
  if (!u || typeof u !== 'object') return false;
  if (
    truthyFlag(u.is_platform_super_admin) ||
    truthyFlag(u.is_super_admin) ||
    truthyFlag(u.isSuperAdmin) ||
    truthyFlag(u.super_admin) ||
    truthyFlag(u.superAdmin)
  ) {
    return true;
  }
  return collectRoleNames(u).some((slug) => slug === 'super_admin' || slug === 'superadmin');
}

/**
 * آدرس تصویر پروفایل کاربر — حرف اول فقط وقتی این مقدار خالی است باید نمایش داده شود.
 *
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {string|null}
 */
export function userAvatarUrl(user) {
  if (!user || typeof user !== 'object') return null;
  const nested = user.data && typeof user.data === 'object' && !Array.isArray(user.data)
    ? user.data
    : null;
  const sources = [user, nested, user.user];
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const candidates = [source.avatar_url, source.avatar, source.profile_photo_url];
    for (const value of candidates) {
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return null;
}

/**
 * نرمال‌سازی آبجکت کاربر API برای UI پنل.
 *
 * @param {unknown} raw
 */
export function mapUserFromApi(raw) {
  const u = unwrapData(raw) ?? raw;
  if (!u || typeof u !== 'object') return null;

  const id = u.id;
  const name =
    (typeof u.name === 'string' && u.name.trim()) ||
    [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
    (typeof u.email === 'string' && u.email) ||
    (typeof u.mobile === 'string' && u.mobile) ||
    (typeof u.username === 'string' && u.username) ||
    (typeof u.identifier === 'string' && u.identifier) ||
    'کاربر';

  const email = (typeof u.email === 'string' && u.email) || '';
  const username = typeof u.username === 'string' ? u.username : '';
  const mobile = typeof u.mobile === 'string' ? u.mobile : '';

  let role = 'عضو';
  const roleNames = collectRoleNames(u);
  if (roleNames.length) {
    role = typeof u.role === 'string' && u.role.trim() ? u.role.trim() : roleNames[0];
  } else if (u.roles && Array.isArray(u.roles) && u.roles.length) {
    const r0 = u.roles[0];
    if (typeof r0 === 'string') role = r0;
    else if (r0 && typeof r0 === 'object' && typeof r0.name === 'string') role = r0.name;
  }

  const permissions = Array.isArray(u.permissions)
    ? u.permissions
    : Array.isArray(u.effective_permissions)
      ? u.effective_permissions
      : [];

  const organization_permissions = Array.isArray(u.organization_permissions)
    ? u.organization_permissions
    : [];
  const organization_access_scopes = Array.isArray(u.organization_access_scopes)
    ? u.organization_access_scopes
    : [];

  const avatar = userAvatarUrl(u);

  const emailVerifiedAt =
    typeof u.email_verified_at === 'string' && u.email_verified_at ? u.email_verified_at : null;
  const mobileVerifiedAt =
    typeof u.mobile_verified_at === 'string' && u.mobile_verified_at ? u.mobile_verified_at : null;

  const isPlatformSuperAdmin = detectSuperAdmin(u);
  const isPlatformAdmin = truthyFlag(u.is_platform_admin)
    || collectRoleNames(u).includes('admin')
    || (Array.isArray(u.platform_roles) && u.platform_roles.some((r) => normalizeRoleSlug(r) === 'admin'));
  const requiresActiveOrganization =
    u.requires_active_organization != null
      ? Boolean(u.requires_active_organization)
      : isPlatformSuperAdmin
        ? false
        : undefined;

  return {
    id,
    name,
    email,
    username,
    mobile,
    role,
    is_super_admin: isPlatformSuperAdmin,
    is_platform_super_admin: isPlatformSuperAdmin,
    is_platform_admin: isPlatformAdmin,
    requires_active_organization: requiresActiveOrganization,
    permissions,
    organization_permissions,
    organization_access_scopes,
    platform_roles: Array.isArray(u.platform_roles) ? u.platform_roles : [],
    avatar,
    is_external: Boolean(u.is_external),
    is_active: u.is_active !== false,
    email_verified_at: emailVerifiedAt,
    mobile_verified_at: mobileVerifiedAt,
    is_email_verified: Boolean(emailVerifiedAt) || Boolean(u.email_verified),
    is_mobile_verified: Boolean(mobileVerifiedAt) || Boolean(u.mobile_verified),
    last_login_at: typeof u.last_login_at === 'string' ? u.last_login_at : null,
    created_at: typeof u.created_at === 'string' ? u.created_at : null,
    updated_at: typeof u.updated_at === 'string' ? u.updated_at : null,
  };
}

/** ادغام فیلدهای سطح بالای پاسخ auth با آبجکت user */
export function mergeAuthUserPayload(user, envelope = {}) {
  if (!user || typeof user !== 'object') return user;
  const merged = { ...user };
  if (truthyFlag(envelope.is_platform_super_admin)) {
    merged.is_platform_super_admin = true;
    merged.is_super_admin = true;
    if (envelope.requires_active_organization == null) {
      merged.requires_active_organization = false;
    }
  }
  if (envelope.requires_active_organization === false) {
    merged.requires_active_organization = false;
  }
  if (envelope.is_super_admin != null && merged.is_super_admin == null) {
    merged.is_super_admin = envelope.is_super_admin;
  }
  if (envelope.requires_active_organization != null) {
    merged.requires_active_organization = envelope.requires_active_organization;
  }
  if (!merged.permissions?.length && Array.isArray(envelope.permissions)) {
    merged.permissions = envelope.permissions;
  }
  if (!merged.organization_permissions?.length && Array.isArray(envelope.organization_permissions)) {
    merged.organization_permissions = envelope.organization_permissions;
  }
  if (!merged.organization_access_scopes?.length && Array.isArray(envelope.organization_access_scopes)) {
    merged.organization_access_scopes = envelope.organization_access_scopes;
  }
  if (!merged.platform_roles?.length && Array.isArray(envelope.platform_roles)) {
    merged.platform_roles = envelope.platform_roles;
  }
  if (!merged.roles?.length && Array.isArray(envelope.roles)) {
    merged.roles = envelope.roles;
  }
  return merged;
}
